<template>
  <WLBaseSelect
    :id="id"
    ref="baseSelectRef"
    v-model:is-open="isOpen"
    :is-disabled="isDisabled"
    :is-full-width="isFullWidth"
    :label="label"
    :label-icon="labelIcon"
    :container-classes="containerClasses"
  >
    <ul
      ref="multiSelectRef"
      class="flex flex-col flex-nowrap items-start gap-1 p-1"
    >
      <li
        v-for="({ label, value, icon }, index) in options"
        :key="index"
        class="w-full"
        @keyup.enter="handleOptionClick(value, !modelValue.has(value))"
        @keydown.up="handleArrowKeys(index - 1)"
        @keydown.down="handleArrowKeys(index + 1)"
        @keydown.tab="handleOptionTab"
      >
        <WLCheckbox
          ref="optionRefs"
          :key="`${index}-checkbox`"
          :input-id="`${id}-option-checkbox-${value}`"
          :auto-focus="index === 0"
          :model-value="modelValue.has(value)"
          :label="label"
          is-boxed
          class="mr-2 w-full"
          @update:model-value="handleOptionClick(value, $event)"
        >
          <template v-if="icon" #suffix>
            <WLIcon :name="icon" :size="IconSize.SMALL" />
          </template>
        </WLCheckbox>
      </li>
    </ul>
  </WLBaseSelect>
</template>

<script lang="ts" setup generic="T extends string">
import { computed, ref, toRefs, onBeforeUpdate } from "vue";
import { onClickOutside } from "@vueuse/core";
import WLBaseSelect from "./BaseSelect.vue";
import { type WLMultiSelectProps } from "./types";
import { WLCheckbox } from "../checkbox";
import { WLIcon, IconSize } from "../icon";

const props = defineProps<WLMultiSelectProps<T>>();

const {
  id,
  options,
  label,
  labelIcon,
  isFullWidth,
  isDisabled,
  containerClasses,
} = toRefs(props);

const modelValue = defineModel<Set<T>>({ required: true });

const isOpen = ref(false);
const baseSelectRef = ref<InstanceType<typeof WLBaseSelect> | null>(null);
const buttonRef = computed(() => baseSelectRef?.value?.buttonRef);
const multiSelectRef = ref<HTMLElement | null>(null);
const optionRefs = ref<Array<typeof WLCheckbox>>([]);

const handleOptionClick = (optionValue: T, isChecked: boolean): void => {
  const cloned = new Set(modelValue.value);

  if (isChecked) {
    cloned.add(optionValue);
  } else {
    cloned.delete(optionValue);
  }

  modelValue.value = cloned;
};

const handleOptionTab = (): void => {
  if (isOpen.value) {
    isOpen.value = false;
    baseSelectRef?.value?.buttonRef?.focus();
  }
};

const handleArrowKeys = (targetIndex: number): void => {
  if (optionRefs.value?.length) {
    const option = optionRefs.value[targetIndex];

    if (option) {
      option?.inputRef?.focus();
      return;
    }

    if (targetIndex < 0) {
      buttonRef?.value?.focus();
    }

    isOpen.value = false;
    baseSelectRef?.value?.buttonRef?.focus();
  }
};

onClickOutside(multiSelectRef, () => (isOpen.value = false), {
  ignore: [buttonRef],
});

onBeforeUpdate(() => {
  optionRefs.value = [];
});
</script>
